/**
 * Screenshot annotation editor.
 *
 * Opens a full-screen overlay inside the widget's shadow root with a canvas
 * editor on top of the screenshot. User can draw with pen, rectangles, or
 * arrows in three colors, undo, clear, save, or cancel. Pure vanilla canvas —
 * zero deps. Touch + pointer support via PointerEvents.
 *
 * Returns a Promise resolving to the annotated PNG Blob (on save) or null
 * (on cancel). Caller is responsible for replacing the queued attachment's
 * blob and regenerating its preview.
 */

import { CLOSE_SVG, PEN_TOOL_SVG, RECT_TOOL_SVG, ARROW_TOOL_SVG, TEXT_TOOL_SVG, UNDO_SVG, TRASH_SVG, CHECK_SVG } from './icons';

type Tool = 'pen' | 'rect' | 'arrow' | 'text';

interface DrawOp {
  tool: Tool;
  color: string;
  width: number;
  // Pen: array of points. Rect/arrow: [start, end]. Text: [anchor].
  points: { x: number; y: number }[];
  // Only for text tool
  text?: string;
  fontSize?: number;
}

const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const TEXT_CSS_PX = 15;          // visible CSS font size when typing
const TEXT_PAD_X = 8;            // horizontal padding inside the text container (canvas-buffer px, scaled)
const TEXT_PAD_Y = 6;            // vertical padding inside the text container
const TEXT_BORDER_WIDTH = 2;     // border width of the text container

const COLORS = [
  { name: 'red',    hex: '#dc2626' },
  { name: 'yellow', hex: '#facc15' },
  { name: 'cyan',   hex: '#06b6d4' },
  { name: 'black',  hex: '#000000' },
] as const;

const STROKE_WIDTH = 4;
const ARROWHEAD_LEN = 14;
const ARROWHEAD_ANGLE = Math.PI / 7;

export async function annotate(blob: Blob, root: ShadowRoot): Promise<Blob | null> {
  const image = await loadBlobToImage(blob);

  return new Promise<Blob | null>((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'df-annot-overlay';

    overlay.innerHTML = `
      <div class="df-annot-frame">
        <div class="df-annot-header">
          <span class="df-annot-title">Annotate screenshot</span>
          <button type="button" class="df-annot-close" aria-label="Cancel">${CLOSE_SVG}</button>
        </div>
        <div class="df-annot-toolbar">
          <div class="df-annot-tools" role="radiogroup" aria-label="Tools">
            <button type="button" class="df-annot-tool" data-tool="pen"   aria-label="Pen"       title="Pen">${PEN_TOOL_SVG}</button>
            <button type="button" class="df-annot-tool" data-tool="rect"  aria-label="Rectangle" title="Rectangle">${RECT_TOOL_SVG}</button>
            <button type="button" class="df-annot-tool" data-tool="arrow" aria-label="Arrow"     title="Arrow">${ARROW_TOOL_SVG}</button>
            <button type="button" class="df-annot-tool" data-tool="text"  aria-label="Text"      title="Text">${TEXT_TOOL_SVG}</button>
          </div>
          <div class="df-annot-divider"></div>
          <div class="df-annot-colors" role="radiogroup" aria-label="Colors">
            ${COLORS.map((c) => `<button type="button" class="df-annot-color" data-color="${c.hex}" aria-label="${c.name}" title="${c.name}" style="background:${c.hex}"></button>`).join('')}
          </div>
          <div class="df-annot-divider"></div>
          <button type="button" class="df-annot-action" data-action="undo"  title="Undo">${UNDO_SVG}<span>Undo</span></button>
          <button type="button" class="df-annot-action" data-action="clear" title="Clear all">${TRASH_SVG}<span>Clear</span></button>
          <div class="df-annot-spacer"></div>
          <button type="button" class="df-annot-cancel">Cancel</button>
          <button type="button" class="df-annot-save">${CHECK_SVG}<span>Save</span></button>
        </div>
        <div class="df-annot-canvas-wrap">
          <canvas class="df-annot-canvas"></canvas>
        </div>
      </div>
    `;
    root.appendChild(overlay);

    const canvas = overlay.querySelector<HTMLCanvasElement>('.df-annot-canvas')!;
    const wrap = overlay.querySelector<HTMLDivElement>('.df-annot-canvas-wrap')!;
    const ctx = canvas.getContext('2d')!;

    // Match canvas pixel buffer to the image's intrinsic resolution.
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    fitCanvasToContainer(canvas, wrap, image);

    let tool: Tool = 'pen';
    let color: string = COLORS[0].hex;
    const ops: DrawOp[] = [];
    let active: DrawOp | null = null;

    const setActiveTool = (t: Tool) => {
      tool = t;
      overlay.querySelectorAll<HTMLButtonElement>('.df-annot-tool').forEach((b) => {
        b.classList.toggle('df-annot-active', b.dataset.tool === t);
      });
    };
    const setActiveColor = (hex: string) => {
      color = hex;
      overlay.querySelectorAll<HTMLButtonElement>('.df-annot-color').forEach((b) => {
        b.classList.toggle('df-annot-active', b.dataset.color === hex);
      });
    };
    setActiveTool('pen');
    setActiveColor(COLORS[0].hex);

    const redraw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0);
      for (const op of ops) drawOp(ctx, op);
      if (active) drawOp(ctx, active);
    };
    redraw();

    // ---- Pointer drawing ----
    const canvasToImageCoords = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * canvas.width;
      const y = ((clientY - rect.top) / rect.height) * canvas.height;
      return { x, y };
    };

    let textInput: HTMLTextAreaElement | null = null;

    /**
     * Open a floating multi-line textarea at the click point. Commits on
     * Cmd/Ctrl+Enter or blur. Esc cancels. Plain Enter adds newlines.
     */
    const spawnTextInput = (clientX: number, clientY: number) => {
      // Commit any existing input first (clicking elsewhere acts like blur)
      if (textInput) commitText();

      const wrapRect = wrap.getBoundingClientRect();
      const canvasRect = canvas.getBoundingClientRect();
      const input = document.createElement('textarea');
      input.className = 'df-annot-text-input';
      input.placeholder = 'Type — Cmd/Ctrl+Enter to save';
      input.rows = 1;
      input.style.left = `${clientX - wrapRect.left + wrap.scrollLeft}px`;
      input.style.top = `${clientY - wrapRect.top + wrap.scrollTop}px`;
      input.style.color = color;
      input.style.fontSize = `${TEXT_CSS_PX}px`;
      input.style.lineHeight = '1.2';
      wrap.appendChild(input);
      textInput = input;

      const anchor = canvasToImageCoords(clientX, clientY);
      // Buffer-pixel font size: scale the CSS size up to match canvas buffer resolution
      const scale = canvasRect.width > 0 ? canvas.width / canvasRect.width : 1;
      const bufferFontSize = Math.round(TEXT_CSS_PX * scale);

      const commit = () => {
        if (!textInput) return;
        // Trim trailing/leading blank lines but preserve internal newlines
        const value = textInput.value.replace(/^\n+|\n+$/g, '').replace(/[ \t]+$/gm, '');
        textInput.remove();
        textInput = null;
        if (value) {
          ops.push({
            tool: 'text',
            color,
            width: STROKE_WIDTH,
            points: [anchor],
            text: value,
            fontSize: bufferFontSize,
          });
          redraw();
        }
      };
      const cancel = () => {
        if (!textInput) return;
        textInput.remove();
        textInput = null;
      };

      input.addEventListener('keydown', (e) => {
        // Cmd/Ctrl+Enter commits; plain Enter adds a newline (browser default)
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
        // Stop the global Esc/Cmd+Z handler from firing while typing
        e.stopPropagation();
      });
      input.addEventListener('blur', commit);

      // Auto-resize: width grows with longest line, height grows with line count.
      const resize = () => {
        input.style.width = 'auto';
        const minW = 80;
        const maxW = Math.max(minW, canvasRect.width - (parseFloat(input.style.left) || 0));
        const idealW = Math.min(maxW, Math.max(minW, input.scrollWidth + 4));
        input.style.width = idealW + 'px';
        input.style.height = 'auto';
        input.style.height = (input.scrollHeight + 2) + 'px';
      };
      input.addEventListener('input', resize);
      resize();
      // Defer focus so the click that spawned us doesn't immediately blur it
      setTimeout(() => input.focus(), 0);
    };

    const commitText = () => {
      if (textInput) textInput.blur(); // triggers commit
    };

    const onDown = (e: PointerEvent) => {
      e.preventDefault();
      // Text tool short-circuits — don't start a drawing op, spawn an input
      if (tool === 'text') {
        spawnTextInput(e.clientX, e.clientY);
        return;
      }
      // Any open text input commits when the user starts drawing elsewhere
      if (textInput) commitText();
      canvas.setPointerCapture(e.pointerId);
      const p = canvasToImageCoords(e.clientX, e.clientY);
      active = { tool, color, width: STROKE_WIDTH, points: [p] };
      redraw();
    };

    const onMove = (e: PointerEvent) => {
      if (!active) return;
      const p = canvasToImageCoords(e.clientX, e.clientY);
      if (active.tool === 'pen') {
        active.points.push(p);
      } else if (active.tool === 'rect' || active.tool === 'arrow') {
        active.points = [active.points[0]!, p];
      }
      redraw();
    };

    const onUp = (e: PointerEvent) => {
      if (!active) return;
      const p = canvasToImageCoords(e.clientX, e.clientY);
      if (active.tool === 'pen') {
        active.points.push(p);
      } else if (active.tool === 'rect' || active.tool === 'arrow') {
        active.points = [active.points[0]!, p];
      }
      // Discard zero-length strokes (accidental click)
      const valid = active.tool === 'pen'
        ? active.points.length > 1
        : distance(active.points[0]!, active.points[1]!) > 4;
      if (valid) ops.push(active);
      active = null;
      redraw();
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);

    // ---- Toolbar event delegation ----
    overlay.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const toolBtn = t.closest<HTMLButtonElement>('[data-tool]');
      if (toolBtn?.dataset.tool) { setActiveTool(toolBtn.dataset.tool as Tool); return; }

      const colorBtn = t.closest<HTMLButtonElement>('[data-color]');
      if (colorBtn?.dataset.color) { setActiveColor(colorBtn.dataset.color); return; }

      const actionBtn = t.closest<HTMLButtonElement>('[data-action]');
      if (actionBtn?.dataset.action === 'undo') { ops.pop(); redraw(); return; }
      if (actionBtn?.dataset.action === 'clear') { ops.length = 0; redraw(); return; }

      if (t.closest('.df-annot-close, .df-annot-cancel')) {
        if (textInput) commitText();
        teardown(null);
        return;
      }
      if (t.closest('.df-annot-save')) {
        // Commit any pending text input before serializing the canvas
        if (textInput) {
          commitText();
          // commitText is sync; redraw runs at end of commit. Wait one frame so
          // toBlob captures the just-committed text op.
          requestAnimationFrame(() => canvas.toBlob((b) => teardown(b ?? null), 'image/png'));
        } else {
          canvas.toBlob((b) => teardown(b ?? null), 'image/png');
        }
        return;
      }
    });

    // ---- Keyboard shortcuts ----
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { teardown(null); return; }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') { ops.pop(); redraw(); e.preventDefault(); }
    };
    document.addEventListener('keydown', onKey);

    // ---- Resize handling ----
    const onResize = () => fitCanvasToContainer(canvas, wrap, image);
    window.addEventListener('resize', onResize);

    function teardown(result: Blob | null) {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(result);
    }
  });
}

// ---------------------------------------------------------------------------

function loadBlobToImage(blob: Blob): Promise<HTMLImageElement> {
  // Use a data: URL instead of URL.createObjectURL — strict host CSPs
  // (img-src 'self' data: https:) reject blob: but allow data:. The widget
  // runs inside customer pages with arbitrary CSPs, so data: is the
  // universally-allowed transport for in-memory image payloads.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = reader.result as string;
    };
    reader.readAsDataURL(blob);
  });
}

function fitCanvasToContainer(canvas: HTMLCanvasElement, wrap: HTMLElement, img: HTMLImageElement): void {
  // Sized via CSS — keep the canvas's pixel buffer at native resolution and
  // let CSS letterbox it inside the container.
  const wrapRect = wrap.getBoundingClientRect();
  const containerRatio = wrapRect.width / wrapRect.height;
  const imageRatio = img.naturalWidth / img.naturalHeight;
  let cssW: number, cssH: number;
  if (imageRatio > containerRatio) {
    cssW = Math.min(wrapRect.width, img.naturalWidth);
    cssH = cssW / imageRatio;
  } else {
    cssH = Math.min(wrapRect.height, img.naturalHeight);
    cssW = cssH * imageRatio;
  }
  canvas.style.width = `${Math.round(cssW)}px`;
  canvas.style.height = `${Math.round(cssH)}px`;
}

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function drawOp(ctx: CanvasRenderingContext2D, op: DrawOp): void {
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = op.color;
  ctx.fillStyle = op.color;
  ctx.lineWidth = op.width;

  if (op.tool === 'text') {
    if (!op.text || op.points.length === 0) return;
    const anchor = op.points[0]!;
    const size = op.fontSize ?? 15;
    const scale = size / TEXT_CSS_PX;
    const padX = TEXT_PAD_X * scale;
    const padY = TEXT_PAD_Y * scale;
    const borderW = Math.max(1, Math.round(TEXT_BORDER_WIDTH * scale));
    const lineHeight = Math.round(size * 1.3);

    ctx.font = `600 ${size}px ${FONT_FAMILY}`;
    ctx.textBaseline = 'top';

    // Measure each line so the bordered container hugs the longest one.
    const lines = op.text.split('\n');
    let maxWidth = 0;
    for (const line of lines) {
      const w = ctx.measureText(line).width;
      if (w > maxWidth) maxWidth = w;
    }
    const totalH = lines.length * lineHeight;

    const boxX = anchor.x;
    const boxY = anchor.y;
    const boxW = maxWidth + padX * 2;
    const boxH = totalH + padY * 2;

    // White fill background — readable on any image content.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(boxX, boxY, boxW, boxH);

    // Colored border using the active annotation color.
    ctx.strokeStyle = op.color;
    ctx.lineWidth = borderW;
    // Inset the stroke so the rendered border stays inside the fill (avoids fractional bleed).
    ctx.strokeRect(boxX + borderW / 2, boxY + borderW / 2, boxW - borderW, boxH - borderW);

    // Text fill on top of the white background.
    ctx.fillStyle = op.color;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!;
      ctx.fillText(line, boxX + padX, boxY + padY + i * lineHeight);
    }
    return;
  }

  if (op.tool === 'pen') {
    if (op.points.length < 2) return;
    const first = op.points[0]!;
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < op.points.length; i++) {
      const p = op.points[i]!;
      ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
    return;
  }

  if (op.points.length < 2) return;
  const a = op.points[0]!;
  const b = op.points[1]!;

  if (op.tool === 'rect') {
    ctx.strokeRect(
      Math.min(a.x, b.x),
      Math.min(a.y, b.y),
      Math.abs(b.x - a.x),
      Math.abs(b.y - a.y),
    );
    return;
  }

  if (op.tool === 'arrow') {
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();

    // Arrowhead — two strokes from the tip
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    ctx.beginPath();
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(b.x - ARROWHEAD_LEN * Math.cos(angle - ARROWHEAD_ANGLE), b.y - ARROWHEAD_LEN * Math.sin(angle - ARROWHEAD_ANGLE));
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(b.x - ARROWHEAD_LEN * Math.cos(angle + ARROWHEAD_ANGLE), b.y - ARROWHEAD_LEN * Math.sin(angle + ARROWHEAD_ANGLE));
    ctx.stroke();
  }
}
